import { useEffect, useState } from 'react';
import cn from 'classnames';

import ActivationPaymentsListItem from './ActivationPaymentsListItem';
import fetchRequest from '../../../utils/fetchRequest';
import PaymentItem from '../../../components/common/features/PaymentItem';
import ActivationPaymentsModal from './ActivationPaymentsModal';
import EllipseIcon from '../../../assets/icons/common/EllipseIcon';
import { useGetListPaymentsQuery } from '../../../redux/paymentsApi';
import { useGetAddressByIdQuery } from '../../../redux/addressesApi';
import {
  RESEND_UPDATE_PAYMENT_LINK_COUNTER,
  CHECK_UPDATE_PAYMENT_COUNTER,
  RESEND_CODE_COUNTER,
} from '../../../constants';

import styles from './styles.module.scss';

const ActivationPaymentsList = ({
  sub,
  selectedPaymentMethodId,
  setSelectedPaymentMethodId,
}) => {
  const { data: paymentsResp, refetch } = useGetListPaymentsQuery(
    {
      include: 'addresses',
    },
    { refetchOnMountOrArgChange: true },
  );
  const { data: addressResp } = useGetAddressByIdQuery(sub.address_id);
  const payments = paymentsResp?.payment_methods || [];
  const [loading, setLoading] = useState(false);
  const [retryCounter, setRetryCounter] = useState(
    CHECK_UPDATE_PAYMENT_COUNTER,
  );
  const [endDate, setEndDate] = useState(0);
  const [resendCounter, setResendCounter] = useState(0);
  const [error, setError] = useState('');
  const [oldPaymentData, setOldPaymentData] = useState([]);
  const selectedPayment = payments.find((payment) => {
    return payment.id === selectedPaymentMethodId;
  });
  const [showDropdown, setShowDropdown] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isPaymentsDataDifferent = (oldPaymentData, newPaymentsData) => {
    for (const oldPayment of oldPaymentData) {
      const newPayment = newPaymentsData.find(
        (payment) => payment.id === oldPayment.id,
      );
      if (!newPayment || newPayment.updated_at !== oldPayment.updated_at) {
        return true;
      }
    }
    return false;
  };

  const changeSelectedPayment = (paymentId) => {
    setSelectedPaymentMethodId(paymentId);
    setShowDropdown(false);
  };

  const handleSendUpdateLink = async () => {
    setError('');
    try {
      await fetchRequest.post(
        `/payment-methods/${selectedPayment?.id}/send_update_email`,
        {
          shopify_gid: selectedPayment?.processor_payment_method_token,
        },
      );
      setOldPaymentData(payments);
      setLoading(true);
      setIsModalOpen(true);
      setResendCounter(RESEND_UPDATE_PAYMENT_LINK_COUNTER);
      setEndDate(Date.now() + RESEND_CODE_COUNTER * 1000);
    } catch (e) {
      setError('An error occurred, please try again later');
      closeDropdown();
    }
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const closeDropdown = () => {
    setShowDropdown(false);
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleDropdownClick = (e) => {
    e.stopPropagation();
  };

  useEffect(() => {
    if (!selectedPaymentMethodId) {
      setSelectedPaymentMethodId(
        addressResp?.payment_method_id || payments[0]?.id || null,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressResp, paymentsResp]);

  useEffect(
    () => {
      if (
        loading &&
        !isPaymentsDataDifferent(oldPaymentData, payments) &&
        retryCounter > 0
      ) {
        const interval = setInterval(async () => {
          setRetryCounter((prevRetryCounter) => prevRetryCounter - 1);
          await refetch();
        }, 5000);
        return () => {
          clearInterval(interval);
        };
      } else {
        setLoading(false);
        setRetryCounter(CHECK_UPDATE_PAYMENT_COUNTER);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading, oldPaymentData, payments, retryCounter],
  );

  const updateCountdown = () => {
    const intervalId = setInterval(() => {
      const currentTime = Date.now();
      const timeDifference = endDate - currentTime;
      let secondsRemaining = Math.floor(timeDifference / 1000);
      if (secondsRemaining >= 0) {
        setResendCounter(secondsRemaining);
      } else {
        clearInterval(intervalId);
      }
    }, 1000);
  };

  useEffect(() => {
    updateCountdown();
    // eslint-disable-next-line
  }, [endDate]);

  useEffect(() => {
    document.body.addEventListener('click', closeDropdown);

    return () => {
      document.body.removeEventListener('click', closeDropdown);
    };
  });

  return (
    <>
      <div className={styles.activationPayments}>
        <p className={cn('text', styles.title)}>Will charge from this card:</p>
        <div
          className={cn(styles.paymentsDropdown, {
            [styles.active]: showDropdown,
          })}
          onClick={handleDropdownClick}
        >
          <div className={styles.paymentsDropdownSelected}>
            {selectedPayment && (
              <ActivationPaymentsListItem
                paymentDetails={selectedPayment}
                clickHandler={toggleDropdown}
                type={'grey'}
                isActive={showDropdown}
              ></ActivationPaymentsListItem>
            )}
            {error && (
              <p className={cn('text-error', styles.paymentsDropdownError)}>
                {error}
              </p>
            )}
          </div>
          <ul className={styles.paymentsDropdownList}>
            {payments.map((payment) => (
              <li key={payment.id}>
                <ActivationPaymentsListItem
                  clickHandler={changeSelectedPayment}
                  paymentDetails={payment}
                  type={
                    payment.id === selectedPaymentMethodId
                      ? 'active'
                      : 'default'
                  }
                ></ActivationPaymentsListItem>
              </li>
            ))}
            {resendCounter ? (
              <li
                key={'update-loading'}
                className={styles.paymentsDropdownUpdatePaymentLoading}
              >
                <EllipseIcon></EllipseIcon>
                Waiting for payment method update... <br />
                You can request payment method update again in {
                  resendCounter
                }{' '}
                seconds
              </li>
            ) : (
              <li
                key={'update-payment'}
                className={styles.paymentsDropdownUpdatePayment}
                onClick={handleSendUpdateLink}
              >
                Update payment method
              </li>
            )}
          </ul>
        </div>
        {selectedPayment && (
          <PaymentItem
            className={styles.paymentItem}
            showUpdate={false}
            paymentDetails={selectedPayment}
          ></PaymentItem>
        )}
      </div>
      <ActivationPaymentsModal
        isOpen={isModalOpen}
        toggleModal={toggleModal}
      ></ActivationPaymentsModal>
    </>
  );
};
export default ActivationPaymentsList;
